import { i18n } from '../../../i18n/index.js';
import { formatDateTime } from '../../../shared/format/index.js';
// Payload подтверждения — это сырой JSON инструмента ассистента. В инбоксе
// пользователь должен понимать, что именно он подтверждает, поэтому payload
// разбирается здесь в текст и ссылку на затронутую сущность.
import type { EntityKind } from '../../../shared/ui/EntityLink.js';
import type {
  ApprovalConsistencyWarning,
  ApprovalItem,
  ApprovalKind,
  ApprovalPresentationV1,
} from '../api/approvals.client.js';
import { parseApprovalConsistencyWarnings } from '../api/approvals.client.js';
import { bulkApprovalItems, DOWNSTREAM_IMPACT_KEYS } from './bulkApprovalSummary.js';

/**
 * Предупреждения сверки «текст пользователя → payload». Пустой список — если
 * сервер не сохранил сверку (старые подтверждения) или расхождений нет.
 */
export function approvalConsistencyWarnings(approval: ApprovalItem): ApprovalConsistencyWarning[] {
  return parseApprovalConsistencyWarnings(approval.payload['consistency']) ?? [];
}

export interface ApprovalDetail {
  label: string;
  value: string;
  /**
   * Whitespace in value (newlines, repeated spaces, tabs) is significant and
   * must be rendered verbatim without collapsing.
   */
  preserveWhitespace?: boolean;
}

export interface ApprovalSummary {
  /** Что произойдёт при подтверждении. */
  action: string;
  /** Разбор аргументов — что именно изменится. */
  details: ApprovalDetail[];
  /** Сущность, которой касается действие. */
  target: { kind: EntityKind; id: string; label?: string } | null;
  presentationTarget?: { label: string };
}

const PRESENTATION_FIELD_KEYS: Record<string, string> = {
  key: 'crm.approvals.detail.memory_key',
  level: 'crm.approvals.detail.memory_level',
  valueText: 'crm.approvals.detail.memory_value',
  importance: 'crm.approvals.detail.memory_importance',
  embedding: 'crm.approvals.detail.memory_embedding',
  batchPosition: 'crm.chat.compound.batch_position',
  title: 'crm.approvals.detail.title',
  undoContext: 'crm.approvals.detail.undo_context',
  name: 'crm.approvals.detail.name',
  firstName: 'crm.approvals.detail.first_name',
  lastName: 'crm.approvals.detail.last_name',
  email: 'crm.approvals.detail.email',
  phone: 'crm.approvals.detail.phone',
  status: 'crm.approvals.detail.status',
  taskStatus: 'crm.approvals.detail.status',
  taskSelection: 'crm.entity_detail.tasks',
  priority: 'crm.approvals.detail.priority',
  dueAt: 'crm.approvals.detail.due_at',
  stage: 'crm.approvals.detail.stage',
  reason: 'crm.approvals.detail.reason',
  amount: 'crm.approvals.bulk.field.amount',
  description: 'crm.approvals.detail.description',
  source: 'crm.approvals.detail.source',
  tags: 'crm.approvals.detail.tags',
  body: 'crm.approvals.detail.description',
  notes: 'crm.approvals.detail.description',
  domain: 'crm.approvals.detail.domain',
  industry: 'crm.approvals.detail.industry',
  website: 'crm.approvals.detail.website',
  nextActivityDue: 'crm.approvals.detail.due_at',
  startDate: 'crm.approvals.detail.start_date',
  dueDate: 'crm.approvals.detail.due_date',
  assignee: 'crm.approvals.detail.assignee',
  owner: 'crm.approvals.detail.owner',
  member: 'crm.approvals.detail.member',
  links: 'crm.approvals.detail.link',
  deal: 'crm.approvals.detail.deal',
  project: 'crm.approvals.detail.project',
  task: 'crm.approvals.detail.task',
  company: 'crm.approvals.detail.company',
  contact: 'crm.approvals.detail.contact',
  document: 'crm.approvals.detail.document',
  entityType: 'crm.approvals.detail.entity_type',
  rows: 'crm.approvals.detail.rows',
};

/**
 * The presentation payload carries raw tool values ("high",
 * "2026-09-21T09:30:00+03:00"). The confirmation card must show the same
 * human-readable change the user is about to confirm, not the payload.
 */
function humanizeApprovalField(fieldKey: string, value: string): string {
  if (fieldKey === 'batchPosition') {
    const [position = '', count = ''] = value.split('/');
    return i18n.t('crm.chat.compound.batch_position_value', { position, count });
  }
  if (fieldKey === 'undoContext' && value === 'task_creation') {
    return i18n.t('crm.approvals.undo_creation_context');
  }
  if (fieldKey === 'embedding' && value === 'external_ai_provider') {
    return i18n.t('crm.approvals.detail.memory_embedding_requested');
  }
  if (
    fieldKey === 'taskStatus' &&
    ['todo', 'in_progress', 'blocked', 'done', 'cancelled'].includes(value)
  ) {
    return i18n.t(`crm.dashboard.status.${value}`);
  }
  if (fieldKey === 'priority') {
    const normalized = value.trim().toLowerCase();
    const key = `crm.chat.action_plan.priority.${normalized}`;
    const label = i18n.t(key as never);
    return label === key ? value : label;
  }
  if (
    fieldKey === 'dueAt' ||
    fieldKey === 'dueDate' ||
    fieldKey === 'startDate' ||
    fieldKey === 'nextActivityDue'
  ) {
    const formatted = formatDateTime(value);
    return formatted === '—' ? value : formatted;
  }
  return value;
}

function presentationValue(value: unknown, preserveWhitespace = false): string | null {
  if (typeof value === 'string') return preserveWhitespace ? value : truncate(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value) && value.every((item) => typeof item === 'string'))
    return value.join(', ');
  return null;
}

export function summarizeApprovalPresentation(
  presentation: ApprovalPresentationV1,
): ApprovalSummary {
  const details = presentation.changes.flatMap((change) => {
    const labelKey = PRESENTATION_FIELD_KEYS[change.fieldKey];
    const label =
      labelKey === undefined ? i18n.t('crm.approvals.detail.field_changed') : i18n.t(labelKey);
    const preserveWhitespace =
      presentation.target.kind === 'memory' && change.fieldKey === 'valueText';
    const before = presentationValue(change.before, preserveWhitespace);
    const after = presentationValue(change.after, preserveWhitespace);
    const value = presentationValue(change.value, preserveWhitespace);
    const rendered =
      before !== null && after !== null
        ? `${i18n.t('crm.approvals.detail.from')}: ${before} → ${i18n.t('crm.approvals.detail.to')}: ${after}`
        : value;
    return rendered === null
      ? []
      : [
          {
            label,
            value: humanizeApprovalField(change.fieldKey, rendered),
            ...(preserveWhitespace ? { preserveWhitespace: true } : {}),
          },
        ];
  });
  if (presentation.impact.downstream) {
    const impact = Object.entries(presentation.impact.downstream)
      .filter(([, count]) => Number.isSafeInteger(count) && count > 0)
      .map(([key, count]) => i18n.t(`crm.approvals.impact.${key}`, { count }))
      .join(', ');
    if (impact) details.push({ label: i18n.t('crm.approvals.impact.label'), value: impact });
  }
  return {
    action: i18n.t(presentation.actionKey),
    details,
    target: null,
    ...(presentation.target.kind === 'bulk'
      ? {
          presentationTarget: {
            label: i18n.t('crm.approvals.bulk.count', { count: presentation.target.label }),
          },
        }
      : presentation.target.kind === 'dashboard_widget'
        ? {}
        : { presentationTarget: { label: presentation.target.label } }),
  };
}

export function summarizeLegacyApproval(approval?: ApprovalItem): ApprovalSummary {
  if (approval === undefined) {
    return {
      action: i18n.t('crm.approvals.detail.review'),
      details: [],
      target: null,
    };
  }
  // Legacy rows have no server presentation, but their persisted canonical
  // payload can still be projected by the bounded allowlist below. Unknown
  // fields, provider summary/diff and identifiers never become visible text.
  return summarizeApproval(approval);
}

/**
 * Сводка для подтверждения, известного только по ожидающему вызову инструмента:
 * чат держит его в форме кита (`toolName` + `args`), без серверной проекции и
 * `kind`. Копия и allowlist полей те же, что у сохранённой строки инбокса, —
 * пользователь видит одинаковый текст до и после перезагрузки.
 */
export function summarizeToolCallApproval(
  toolName: string,
  toolArgs: Record<string, unknown>,
): ApprovalSummary {
  return summarizeApproval({
    id: '',
    kind: 'tool_call',
    status: 'pending',
    payload: { toolName, args: toolArgs },
    expiresAt: '',
  });
}

const KIND_ACTION: Record<ApprovalKind, string> = {
  get tool_call() {
    return i18n.t('crm.features.map.features.approvals.lib.approvalsummary.6');
  },
  get scenario_step() {
    return i18n.t('crm.features.map.features.approvals.lib.approvalsummary.7');
  },
  get contact_delete() {
    return i18n.t('crm.features.map.features.approvals.lib.approvalsummary.8');
  },
  get deal_delete() {
    return i18n.t('crm.features.map.features.approvals.lib.approvalsummary.9');
  },
};

function str(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function args(payload: Record<string, unknown>): Record<string, unknown> {
  const value = payload.args;
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const NOTE_LINK_ENTITY_KINDS = new Set<EntityKind>([
  'deal',
  'company',
  'contact',
  'task',
  'project',
  'document',
  'template',
]);

function canonicalNoteLinkTarget(toolArgs: Record<string, unknown>): ApprovalSummary['target'] {
  const links = toolArgs.links;
  if (!Array.isArray(links)) return null;

  for (const link of links) {
    if (typeof link !== 'object' || link === null || Array.isArray(link)) continue;
    const { namespace, id } = link as Record<string, unknown>;
    if (
      typeof namespace === 'string' &&
      NOTE_LINK_ENTITY_KINDS.has(namespace as EntityKind) &&
      typeof id === 'string' &&
      id.trim().length > 0
    ) {
      const label = ['label', 'title', 'name']
        .map((key) => (link as Record<string, unknown>)[key])
        .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
      return {
        kind: namespace as EntityKind,
        id,
        ...(label === undefined ? {} : { label: truncate(label) }),
      };
    }
  }

  return null;
}

function downstreamImpact(payload: Record<string, unknown>): ApprovalDetail | null {
  const preview =
    typeof payload.preview === 'object' && payload.preview !== null
      ? (payload.preview as Record<string, unknown>)
      : {};
  const source = preview.downstreamImpact ?? preview.impact;
  if (typeof source !== 'object' || source === null || Array.isArray(source)) return null;
  const values = DOWNSTREAM_IMPACT_KEYS.flatMap((key) => {
    const count = (source as Record<string, unknown>)[key];
    return typeof count === 'number' && Number.isSafeInteger(count) && count > 0
      ? [i18n.t(`crm.approvals.impact.${key}`, { count })]
      : [];
  });
  return values.length > 0
    ? { label: i18n.t('crm.approvals.impact.label'), value: values.join(', ') }
    : null;
}

function truncate(value: string, limit = 160): string {
  const normalized = Array.from(value)
    .map((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  const characters = Array.from(normalized);
  return characters.length > limit ? `${characters.slice(0, limit - 1).join('')}…` : normalized;
}

const TASK_ACTION_KEYS: Record<string, string> = {
  create_task: 'crm.approvals.summary.create_task',
  update_task: 'crm.approvals.summary.update_task',
  delete_task: 'crm.approvals.summary.delete_task',
  set_task_status: 'crm.approvals.summary.set_task_status',
  assign_task: 'crm.approvals.summary.assign_task',
  link_task: 'crm.approvals.summary.link_task',
  unlink_task: 'crm.approvals.summary.unlink_task',
  set_task_due_date: 'crm.approvals.summary.set_task_due_date',
  create_note: 'crm.approvals.summary.create_note',
  update_note: 'crm.approvals.summary.update_note',
  delete_note: 'crm.approvals.summary.delete_note',
  link_note: 'crm.approvals.summary.link_note',
  unlink_note: 'crm.approvals.summary.unlink_note',
  create_contact: 'crm.approvals.summary.create_contact',
  update_contact: 'crm.approvals.summary.update_contact',
  delete_contact: 'crm.approvals.summary.delete_contact',
  set_contact_tags: 'crm.approvals.summary.set_contact_tags',
  set_contact_owner: 'crm.approvals.summary.set_contact_owner',
  link_contact_company: 'crm.approvals.summary.link_contact_company',
  unlink_contact_company: 'crm.approvals.summary.unlink_contact_company',
  create_contact_interaction: 'crm.approvals.summary.create_contact_interaction',
  update_contact_interaction: 'crm.approvals.summary.update_contact_interaction',
  delete_contact_interaction: 'crm.approvals.summary.delete_contact_interaction',
  create_company: 'crm.approvals.summary.create_company',
  update_company: 'crm.approvals.summary.update_company',
  delete_company: 'crm.approvals.summary.delete_company',
  create_project: 'crm.approvals.summary.create_project',
  add_project_member: 'crm.approvals.summary.add_project_member',
  remove_project_member: 'crm.approvals.summary.remove_project_member',
  update_project: 'crm.approvals.summary.update_project',
  delete_project: 'crm.approvals.summary.delete_project',
  set_company_tags: 'crm.approvals.summary.set_company_tags',
  set_company_owner: 'crm.approvals.summary.set_company_owner',
  link_company_relation: 'crm.approvals.summary.link_company_relation',
  unlink_company_relation: 'crm.approvals.summary.unlink_company_relation',
  create_calendar_event: 'crm.approvals.summary.create_calendar_event',
  update_calendar_event: 'crm.approvals.summary.update_calendar_event',
  cancel_calendar_event: 'crm.approvals.summary.cancel_calendar_event',
  reschedule_calendar_event: 'crm.approvals.summary.reschedule_calendar_event',
  link_calendar_event_participant: 'crm.approvals.summary.link_calendar_event_participant',
  unlink_calendar_event_participant: 'crm.approvals.summary.unlink_calendar_event_participant',
  create_document: 'crm.approvals.summary.create_document',
  update_document: 'crm.approvals.summary.update_document',
  delete_document: 'crm.approvals.summary.delete_document',
  link_document: 'crm.approvals.summary.link_document',
  unlink_document: 'crm.approvals.summary.unlink_document',
  transition_document_status: 'crm.approvals.summary.transition_document_status',
  create_template: 'crm.approvals.summary.create_template',
  update_template: 'crm.approvals.summary.update_template',
  delete_template: 'crm.approvals.summary.delete_template',
  activate_template: 'crm.approvals.summary.activate_template',
  deactivate_template: 'crm.approvals.summary.deactivate_template',
  create_text_attachment: 'crm.approvals.summary.create_text_attachment',
  update_text_attachment: 'crm.approvals.summary.update_text_attachment',
  link_attachment: 'crm.approvals.summary.link_attachment',
  unlink_attachment: 'crm.approvals.summary.unlink_attachment',
  delete_attachment: 'crm.approvals.summary.delete_attachment',
};

const BULK_ACTION_KEYS: Record<string, string> = {
  bulk_create_deals: 'crm.approvals.bulk.summary.create_deals',
  bulk_update_deals: 'crm.approvals.bulk.summary.update_deals',
  bulk_delete_deals: 'crm.approvals.bulk.summary.delete_deals',
  bulk_set_deal_stage: 'crm.approvals.bulk.summary.set_deal_stage',
  bulk_set_deal_owner: 'crm.approvals.bulk.summary.set_deal_owner',
  bulk_set_deal_tags: 'crm.approvals.bulk.summary.set_deal_tags',
  bulk_create_tasks: 'crm.approvals.bulk.summary.create_tasks',
  bulk_update_tasks: 'crm.approvals.bulk.summary.update_tasks',
  bulk_delete_tasks: 'crm.approvals.bulk.summary.delete_tasks',
  bulk_set_task_status: 'crm.approvals.bulk.summary.set_task_status',
  bulk_set_task_assignee: 'crm.approvals.bulk.summary.set_task_assignee',
  bulk_set_task_due_date: 'crm.approvals.bulk.summary.set_task_due_date',
  bulk_create_notes: 'crm.approvals.bulk.summary.create_notes',
  bulk_update_notes: 'crm.approvals.bulk.summary.update_notes',
  bulk_delete_notes: 'crm.approvals.bulk.summary.delete_notes',
  bulk_link_notes: 'crm.approvals.bulk.summary.link_notes',
  bulk_unlink_notes: 'crm.approvals.bulk.summary.unlink_notes',
  bulk_create_contacts: 'crm.approvals.bulk.summary.create_contacts',
  bulk_update_contacts: 'crm.approvals.bulk.summary.update_contacts',
  bulk_delete_contacts: 'crm.approvals.bulk.summary.delete_contacts',
  bulk_set_contact_tags: 'crm.approvals.bulk.summary.set_contact_tags',
  bulk_set_contact_owner: 'crm.approvals.bulk.summary.set_contact_owner',
  bulk_link_contact_company: 'crm.approvals.bulk.summary.link_contact_company',
  bulk_unlink_contact_company: 'crm.approvals.bulk.summary.unlink_contact_company',
  bulk_create_companies: 'crm.approvals.bulk.summary.create_companies',
  bulk_update_companies: 'crm.approvals.bulk.summary.update_companies',
  bulk_delete_companies: 'crm.approvals.bulk.summary.delete_companies',
  bulk_set_company_tags: 'crm.approvals.bulk.summary.set_company_tags',
  bulk_set_company_owner: 'crm.approvals.bulk.summary.set_company_owner',
  bulk_link_companies: 'crm.approvals.bulk.summary.link_companies',
  bulk_unlink_companies: 'crm.approvals.bulk.summary.unlink_companies',
  bulk_create_calendar_events: 'crm.approvals.bulk.summary.create_calendar_events',
  bulk_update_calendar_events: 'crm.approvals.bulk.summary.update_calendar_events',
  bulk_cancel_calendar_events: 'crm.approvals.bulk.summary.cancel_calendar_events',
  bulk_reschedule_calendar_events: 'crm.approvals.bulk.summary.reschedule_calendar_events',
  bulk_link_calendar_event_participants:
    'crm.approvals.bulk.summary.link_calendar_event_participants',
  bulk_unlink_calendar_event_participants:
    'crm.approvals.bulk.summary.unlink_calendar_event_participants',
  bulk_create_contact_interactions: 'crm.approvals.bulk.summary.create_contact_interactions',
  bulk_update_contact_interactions: 'crm.approvals.bulk.summary.update_contact_interactions',
  bulk_delete_contact_interactions: 'crm.approvals.bulk.summary.delete_contact_interactions',
  bulk_create_documents: 'crm.approvals.bulk.summary.create_documents',
  bulk_update_documents: 'crm.approvals.bulk.summary.update_documents',
  bulk_delete_documents: 'crm.approvals.bulk.summary.delete_documents',
  bulk_create_templates: 'crm.approvals.bulk.summary.create_templates',
  bulk_update_templates: 'crm.approvals.bulk.summary.update_templates',
  bulk_delete_templates: 'crm.approvals.bulk.summary.delete_templates',
  bulk_activate_templates: 'crm.approvals.bulk.summary.activate_templates',
  bulk_deactivate_templates: 'crm.approvals.bulk.summary.deactivate_templates',
  bulk_link_attachments: 'crm.approvals.bulk.summary.link_attachments',
  bulk_unlink_attachments: 'crm.approvals.bulk.summary.unlink_attachments',
  bulk_delete_attachments: 'crm.approvals.bulk.summary.delete_attachments',
};

function bulkSummary(toolName: string, toolArgs: Record<string, unknown>): ApprovalSummary | null {
  const actionKey = BULK_ACTION_KEYS[toolName];
  if (!actionKey) return null;
  const count = Math.min(bulkApprovalItems(toolName, toolArgs).length, 100);
  return {
    action: i18n.t(actionKey),
    details: [
      {
        label: i18n.t('crm.approvals.execution_result'),
        value: i18n.t('crm.approvals.bulk.count', { count }),
      },
    ],
    target: null,
  };
}

function taskOrNoteSummary(
  toolName: string,
  toolArgs: Record<string, unknown>,
): ApprovalSummary | null {
  const actionKey = TASK_ACTION_KEYS[toolName];
  if (!actionKey) return null;
  const details: ApprovalDetail[] = [];
  const add = (key: string, labelKey: string) => {
    const value = toolArgs[key];
    if (typeof value === 'string' && value.trim())
      details.push({ label: i18n.t(labelKey), value: truncate(value) });
  };
  add('title', 'crm.approvals.detail.title');
  add('fileName', 'crm.approvals.detail.file_name');
  add('status', 'crm.approvals.detail.status');
  // Приоритет и срок приходят из инструмента сырыми ("high",
  // "2026-09-21T12:00:00+03:00"): в карточке подтверждения пользователь должен
  // видеть человеческие значения, а не машинные идентификаторы.
  const priority = toolArgs['priority'];
  if (typeof priority === 'string' && priority.trim()) {
    details.push({
      label: i18n.t('crm.approvals.detail.priority'),
      value: i18n.t(`crm.chat.action_plan.priority.${priority.trim().toLowerCase()}`),
    });
  }
  const dueAt = toolArgs['dueAt'];
  if (typeof dueAt === 'string' && dueAt.trim()) {
    details.push({
      label: i18n.t('crm.approvals.detail.due_at'),
      value: formatDateTime(dueAt),
    });
  }
  add('startDate', 'crm.approvals.detail.start_date');
  add('dueDate', 'crm.approvals.detail.due_date');
  add('assigneeMemberId', 'crm.approvals.detail.assignee');
  add('link', 'crm.approvals.detail.link');
  add('firstName', 'crm.approvals.detail.first_name');
  add('lastName', 'crm.approvals.detail.last_name');
  add('name', 'crm.approvals.detail.name');
  add('phone', 'crm.approvals.detail.phone');
  add('email', 'crm.approvals.detail.email');
  add('role', 'crm.approvals.detail.role');
  add('type', 'crm.approvals.detail.interaction_type');
  add('subject', 'crm.approvals.detail.subject');
  const relation = toolArgs.relation;
  if (
    typeof relation === 'object' &&
    relation !== null &&
    typeof (relation as Record<string, unknown>).namespace === 'string'
  ) {
    details.push({
      label: i18n.t('crm.approvals.detail.link'),
      value: truncate((relation as Record<string, unknown>).namespace as string),
    });
  }
  const taskId = str(toolArgs, 'taskId');
  const contactId = str(toolArgs, 'contactId');
  const companyId = str(toolArgs, 'companyId');
  const taskLabel = str(toolArgs, 'taskTitle') ?? str(toolArgs, 'title');
  const contactLabel =
    str(toolArgs, 'contactName') ??
    ([str(toolArgs, 'firstName'), str(toolArgs, 'lastName')].filter(Boolean).join(' ').trim() ||
      null);
  const companyLabel = str(toolArgs, 'companyName') ?? str(toolArgs, 'name');
  const target = taskId
    ? {
        kind: 'task' as const,
        id: taskId,
        ...(taskLabel ? { label: truncate(taskLabel) } : {}),
      }
    : contactId
      ? {
          kind: 'contact' as const,
          id: contactId,
          ...(contactLabel ? { label: truncate(contactLabel) } : {}),
        }
      : companyId
        ? {
            kind: 'company' as const,
            id: companyId,
            ...(companyLabel ? { label: truncate(companyLabel) } : {}),
          }
        : toolName === 'create_note'
          ? canonicalNoteLinkTarget(toolArgs)
          : null;
  return {
    action: i18n.t(actionKey),
    details,
    target,
  };
}

export function summarizeApproval(approval: ApprovalItem): ApprovalSummary {
  const payload = approval.payload ?? {};

  if (approval.kind === 'contact_delete') {
    const contactId = str(payload, 'contactId');
    return {
      action: i18n.t('crm.features.features.approvals.lib.approvalsummary.удалить_контакт'),
      details: [],
      target: contactId ? { kind: 'contact', id: contactId } : null,
    };
  }

  if (approval.kind === 'deal_delete') {
    const dealId = str(payload, 'dealId');
    return {
      action: i18n.t('crm.features.features.approvals.lib.approvalsummary.удалить_сделку'),
      details: [],
      target: dealId ? { kind: 'deal', id: dealId } : null,
    };
  }

  if (approval.kind === 'scenario_step') {
    const scenarioKey = str(payload, 'scenarioKey');
    const stepId = str(payload, 'stepId');
    if (scenarioKey === 'follow-up-after-3-days' && stepId === 'send_follow_up_email') {
      const dealId = str(payload, 'dealId');
      const dealTitle = str(payload, 'dealTitle');
      const toEmail = str(payload, 'toEmail');
      const subject = str(payload, 'subject');
      const details: ApprovalDetail[] = [];
      if (dealTitle)
        details.push({ label: i18n.t('crm.approvals.detail.deal'), value: truncate(dealTitle) });
      if (toEmail)
        details.push({
          label: i18n.t('crm.approvals.scenario_step.follow_up.recipient'),
          value: toEmail,
        });
      if (subject)
        details.push({
          label: i18n.t('crm.approvals.scenario_step.follow_up.subject'),
          value: truncate(subject),
        });
      return {
        action: i18n.t('crm.approvals.scenario_step.follow_up.action'),
        details,
        target: dealId
          ? { kind: 'deal', id: dealId, ...(dealTitle ? { label: truncate(dealTitle) } : {}) }
          : null,
      };
    }
    // Workflow-graph approvals carry a bounded safe payload: the node id,
    // the compiler-declared requirement and — for a protected destructive
    // email — the exact frozen action (deal title, recipient, subject, body).
    // No checkpoint, plan hash, args digest or tool outputs ever reach this
    // layer.
    if (str(payload, 'source') === 'workflow_graph') {
      const workflowDetails: ApprovalDetail[] = [];
      const graphNodeId = str(payload, 'nodeId');
      if (graphNodeId)
        workflowDetails.push({
          label: i18n.t('crm.approvals.scenario_step.workflow.step'),
          value: truncate(graphNodeId),
        });
      // The raw enum never reaches the user: the requirement value is
      // localized exactly like the label.
      const requires = str(payload, 'requires');
      if (requires === 'destructive')
        workflowDetails.push({
          label: i18n.t('crm.approvals.scenario_step.workflow.requires'),
          value: i18n.t('crm.approvals.scenario_step.workflow.requires_destructive'),
        });
      const protectedTool = payload['protectedTool'];
      const bound =
        typeof protectedTool === 'object' && protectedTool !== null && !Array.isArray(protectedTool)
          ? (protectedTool as Record<string, unknown>)
          : null;
      const dealTitle = bound === null ? null : str(bound, 'dealTitle');
      const dealId = bound === null ? null : str(bound, 'dealId');
      const recipient = bound === null ? null : str(bound, 'recipient');
      // Subject and body are read VERBATIM (bounded by the canonical schema):
      // collapsing whitespace here would misrepresent the approved text.
      const subject =
        bound === null || typeof bound['subject'] !== 'string' || bound['subject'].trim() === ''
          ? null
          : bound['subject'];
      const body =
        bound === null || typeof bound['body'] !== 'string' || bound['body'].trim() === ''
          ? null
          : bound['body'];
      if (bound !== null && recipient !== null && subject !== null && body !== null) {
        return {
          action: i18n.t('crm.approvals.scenario_step.workflow_email.action'),
          details: [
            ...(dealTitle
              ? [{ label: i18n.t('crm.approvals.detail.deal'), value: truncate(dealTitle) }]
              : []),
            {
              label: i18n.t('crm.approvals.scenario_step.workflow_email.recipient'),
              value: recipient,
            },
            {
              // Frozen email fields ALWAYS preserve whitespace - repeated
              // spaces, tabs and newlines are part of what the user approved.
              label: i18n.t('crm.approvals.scenario_step.workflow_email.subject'),
              value: subject,
              preserveWhitespace: true,
            },
            {
              label: i18n.t('crm.approvals.scenario_step.workflow_email.body'),
              value: body,
              preserveWhitespace: true,
            },
          ],
          // The deal id comes from the server-frozen action snapshot: it was
          // read tenant-scoped at park time (a missing deal never parks an
          // approval), so it doubles as the standard deal-card link. A missing
          // title falls back to the same resolved-label lookup as other rows.
          target: dealId
            ? { kind: 'deal', id: dealId, ...(dealTitle ? { label: truncate(dealTitle) } : {}) }
            : null,
        };
      }
      return {
        action: i18n.t('crm.approvals.scenario_step.workflow.action'),
        details: workflowDetails,
        target: null,
      };
    }
    const details: ApprovalDetail[] = [];
    const scenarioId = str(payload, 'scenarioId');
    if (scenarioId)
      details.push({
        label: i18n.t('crm.features.features.approvals.lib.approvalsummary.сценарий'),
        value: scenarioId,
      });
    if (stepId)
      details.push({
        label: i18n.t('crm.features.features.approvals.lib.approvalsummary.шаг'),
        value: stepId,
      });
    return {
      action: i18n.t('crm.features.features.approvals.lib.approvalsummary.выполнить_шаг_сценария'),
      details,
      target: null,
    };
  }

  const toolName = str(payload, 'toolName');
  const toolArgs = args(payload);
  const bulk = bulkSummary(toolName ?? '', toolArgs);
  if (bulk) {
    const impact = downstreamImpact(payload);
    return impact ? { ...bulk, details: [...bulk.details, impact] } : bulk;
  }
  const taskOrNote = taskOrNoteSummary(toolName ?? '', toolArgs);
  if (taskOrNote) {
    const impact = downstreamImpact(payload);
    return impact ? { ...taskOrNote, details: [...taskOrNote.details, impact] } : taskOrNote;
  }

  switch (toolName) {
    case 'update_deal_stage': {
      const dealId = typeof toolArgs.dealId === 'string' ? toolArgs.dealId : null;
      const details: ApprovalDetail[] = [];
      if (typeof toolArgs.dealTitle === 'string') {
        details.push({
          label: i18n.t('crm.approvals.detail.deal'),
          value: truncate(toolArgs.dealTitle),
        });
      }
      if (typeof toolArgs.fromStage === 'string') {
        details.push({
          label: i18n.t('crm.approvals.detail.from_stage'),
          value: toolArgs.fromStage,
        });
      }
      if (typeof toolArgs.toStage === 'string') {
        details.push({
          label: i18n.t('crm.approvals.detail.to_stage'),
          value: toolArgs.toStage,
        });
      }
      if (typeof toolArgs.reason === 'string' && toolArgs.reason.length > 0) {
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.причина'),
          value: truncate(toolArgs.reason),
        });
      }
      return {
        action: i18n.t('crm.features.features.approvals.lib.approvalsummary.сменить_стадию_сделки'),
        details,
        target: dealId
          ? {
              kind: 'deal',
              id: dealId,
              ...(typeof toolArgs.dealTitle === 'string'
                ? { label: truncate(toolArgs.dealTitle) }
                : {}),
            }
          : null,
      };
    }

    case 'enrich_contact': {
      const contactId = typeof toolArgs.contactId === 'string' ? toolArgs.contactId : null;
      const patch =
        typeof toolArgs.patch === 'object' && toolArgs.patch !== null
          ? (toolArgs.patch as Record<string, unknown>)
          : {};
      const details = Object.entries(patch)
        .filter(([, value]) => typeof value === 'string' && value.length > 0)
        .map(([key, value]) => ({ label: key, value: truncate(String(value)) }));
      return {
        action: i18n.t(
          'crm.features.features.approvals.lib.approvalsummary.дополнить_данные_контакта',
        ),
        details,
        target: contactId ? { kind: 'contact', id: contactId } : null,
      };
    }

    case 'send_email': {
      const dealId = typeof toolArgs.dealId === 'string' ? toolArgs.dealId : null;
      const details: ApprovalDetail[] = [];
      if (typeof toolArgs.toEmail === 'string')
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.кому'),
          value: toolArgs.toEmail,
        });
      if (typeof toolArgs.subject === 'string')
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.тема'),
          value: truncate(toolArgs.subject),
        });
      if (typeof toolArgs.body === 'string')
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.текст'),
          value: truncate(toolArgs.body),
        });
      return {
        action: i18n.t('crm.features.features.approvals.lib.approvalsummary.отправить_письмо'),
        details,
        target: dealId ? { kind: 'deal', id: dealId } : null,
      };
    }

    case 'run_scenario': {
      const context =
        typeof toolArgs.context === 'object' && toolArgs.context !== null
          ? (toolArgs.context as Record<string, unknown>)
          : {};
      const dealId = typeof context.dealId === 'string' ? context.dealId : null;
      const contactId = typeof context.contactId === 'string' ? context.contactId : null;
      const details: ApprovalDetail[] = [];
      if (typeof toolArgs.scenarioId === 'string') {
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.сценарий_2'),
          value: toolArgs.scenarioId,
        });
      }
      return {
        action: i18n.t('crm.features.features.approvals.lib.approvalsummary.запустить_сценарий'),
        details,
        target: dealId
          ? { kind: 'deal', id: dealId }
          : contactId
            ? { kind: 'contact', id: contactId }
            : null,
      };
    }

    case 'write_memory': {
      const details: ApprovalDetail[] = [];
      if (typeof toolArgs.level === 'string')
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.уровень'),
          value: toolArgs.level,
        });
      if (typeof toolArgs.key === 'string')
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.ключ'),
          value: toolArgs.key,
        });
      if (typeof toolArgs.valueText === 'string') {
        details.push({
          label: i18n.t('crm.features.features.approvals.lib.approvalsummary.значение'),
          value: truncate(toolArgs.valueText),
        });
      }
      return {
        action: i18n.t('crm.features.features.approvals.lib.approvalsummary.записать_в_память'),
        details,
        target: null,
      };
    }

    default: {
      return {
        action: toolName ? i18n.t('crm.approvals.summary.generic') : KIND_ACTION[approval.kind],
        details: [],
        target: null,
      };
    }
  }
}

/** Подтверждение просрочено, даже если статус в базе ещё `pending`. */
export function isApprovalExpired(approval: ApprovalItem, now = Date.now()): boolean {
  if (approval.status !== 'pending') return approval.status === 'expired';
  const expiresAt = new Date(approval.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt <= now;
}
